import React, { useCallback, useRef } from 'react';
import {
  CX_EMAIL_HEADER_LOGO_SCALE_OPTIONS,
  CX_EMAIL_HEADER_LOGO_SCALE_STEP,
  normalizeHeaderLogoScale,
} from '../utils/cxEmailTemplateDefaults';

function scaleToPosition(value, min, max) {
  if (max <= min) return 0;
  return ((value - min) / (max - min)) * 100;
}

function valueFromRatio(ratio, min, max) {
  const clamped = Math.max(0, Math.min(1, ratio));
  const raw = min + clamped * (max - min);
  const stepped = Math.round(raw / CX_EMAIL_HEADER_LOGO_SCALE_STEP) * CX_EMAIL_HEADER_LOGO_SCALE_STEP;
  return normalizeHeaderLogoScale(Math.max(min, Math.min(max, stepped)));
}

export default function CxEmailHeaderScaleSlider({ value, onChange }) {
  const normalized = normalizeHeaderLogoScale(value);
  const min = CX_EMAIL_HEADER_LOGO_SCALE_OPTIONS[0];
  const max = CX_EMAIL_HEADER_LOGO_SCALE_OPTIONS[CX_EMAIL_HEADER_LOGO_SCALE_OPTIONS.length - 1];
  const trackRef = useRef(null);
  const thumbLeft = scaleToPosition(normalized, min, max);

  const pickFromClientX = useCallback(
    (clientX) => {
      const track = trackRef.current;
      if (!track) return;
      const rect = track.getBoundingClientRect();
      const ratio = (clientX - rect.left) / rect.width;
      onChange(valueFromRatio(ratio, min, max));
    },
    [min, max, onChange]
  );

  const handlePointerDown = (event) => {
    event.preventDefault();
    pickFromClientX(event.clientX);

    const onMove = (moveEvent) => pickFromClientX(moveEvent.clientX);
    const onUp = () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
  };

  const handleKeyDown = (event) => {
    const idx = CX_EMAIL_HEADER_LOGO_SCALE_OPTIONS.indexOf(normalized);
    if (event.key === 'ArrowRight' || event.key === 'ArrowUp') {
      event.preventDefault();
      onChange(CX_EMAIL_HEADER_LOGO_SCALE_OPTIONS[Math.min(idx + 1, CX_EMAIL_HEADER_LOGO_SCALE_OPTIONS.length - 1)]);
    } else if (event.key === 'ArrowLeft' || event.key === 'ArrowDown') {
      event.preventDefault();
      onChange(CX_EMAIL_HEADER_LOGO_SCALE_OPTIONS[Math.max(idx - 1, 0)]);
    }
  };

  return (
    <div className="cx-email-header-scale">
      <div className="cx-email-header-scale-control">
        <div className="cx-email-header-scale-rail">
          <div
            ref={trackRef}
            className="cx-email-header-scale-track cx-email-header-scale-track--custom"
            role="slider"
            aria-valuemin={min}
            aria-valuemax={max}
            aria-valuenow={normalized}
            aria-label="Tamanho do header da logomarca"
            tabIndex={0}
            onPointerDown={handlePointerDown}
            onKeyDown={handleKeyDown}
          >
            <div className="cx-email-header-scale-line" aria-hidden="true" />
            <div
              className="cx-email-header-scale-fill"
              style={{ width: `${thumbLeft}%` }}
              aria-hidden="true"
            />
            <div
              className="cx-email-header-scale-thumb"
              style={{ left: `${thumbLeft}%` }}
              aria-hidden="true"
            />
          </div>
          <div className="cx-email-header-scale-ticks" aria-hidden="true">
            {CX_EMAIL_HEADER_LOGO_SCALE_OPTIONS.map((pct) => {
              const left = scaleToPosition(pct, min, max);
              const edgeClass = pct === min
                ? ' cx-email-header-scale-tick--start'
                : pct === max
                  ? ' cx-email-header-scale-tick--end'
                  : '';
              return (
                <span
                  key={pct}
                  className={`cx-email-header-scale-tick${edgeClass}`}
                  style={{ left: `${left}%` }}
                >
                  {pct}%
                </span>
              );
            })}
          </div>
        </div>
        <span className="cx-email-header-scale-value">
          {normalized === 100 ? '100% — automático' : `${normalized}%`}
        </span>
      </div>
    </div>
  );
}
